import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CalendarDays,
  Download,
  FileSpreadsheet,
  FileText,
  Filter,
  Inbox,
  Info,
  Landmark,
  Loader2,
  Play,
  Printer,
  RefreshCw,
  RotateCcw,
  Table2,
} from "lucide-react";
import API from "../../services/api";
import PayrollRunSelect from "../../components/common/PayrollRunSelect";
import ReportsEnterpriseShell from "../reports/components/ReportsEnterpriseShell";
import { useReportsNavigation } from "../reports/useReportsNavigation";
import { PAYROLL_HUB_REPORTS } from "../reports/PayrollReportsWorkspace";
import { exportRowsToPdf, exportRowsToXlsx } from "../reports/payrollReportsUtils";
import { printRowsAsTable } from "../reports/reportPrint";
import { downloadBlob } from "../Employees/employeeFileUtils";
import { useToast } from "../Context/ToastContext";
import {
  getBankTransferRegister,
  getBankWiseSummary,
  getBranchWiseSummary,
  getDepartmentWiseSummary,
  getFailedPayments,
  getPendingPayments,
  getSalaryPaymentRegister,
} from "./disbursementReportsApi";
import { bankApiError } from "./bankErrorUtils";
import "../../styles/payrollReportsWorkspace.css";

const REPORTS = [
  {
    id: "bank-transfer",
    label: "Bank Transfer Register",
    needsPeriod: false,
    info: "Every salary payment sent to the bank: employee, bank, IFSC, account (last 4), amount, status and UTR.",
  },
  {
    id: "salary-register",
    label: "Salary Payment Register",
    needsPeriod: true,
    info: "Per-employee salary paid for the selected pay period, with how many payment items succeeded or failed.",
  },
  {
    id: "failed",
    label: "Failed Payments",
    needsPeriod: false,
    info: "Payments the bank rejected, with the failure reason and attempts — fix bank details and retry.",
  },
  {
    id: "pending",
    label: "Pending Payments",
    needsPeriod: false,
    info: "Payments not yet confirmed by the bank (queued, sent or awaiting response).",
  },
  {
    id: "bank-wise",
    label: "Bank-wise Summary",
    needsPeriod: false,
    info: "Totals per bank: number of payments, amount, succeeded and failed.",
  },
  {
    id: "branch-wise",
    label: "Branch-wise Summary",
    needsPeriod: false,
    info: "Totals per bank branch (IFSC): number of payments and amount.",
  },
  {
    id: "department",
    label: "Department-wise Summary",
    needsPeriod: false,
    info: "Totals per department: number of payments and amount.",
  },
];

const COLUMNS = {
  "bank-transfer": [
    { key: "employee_code", label: "Employee Code" },
    { key: "employee_name", label: "Employee Name" },
    { key: "bank_name", label: "Bank" },
    { key: "ifsc_code", label: "IFSC Code" },
    { key: "account_number_last4", label: "Account (Last 4)" },
    { key: "amount", label: "Amount", money: true },
    { key: "status", label: "Status" },
    { key: "payout_ref", label: "UTR / Reference" },
  ],
  "salary-register": [
    { key: "employee_code", label: "Employee Code" },
    { key: "employee_name", label: "Employee Name" },
    { key: "total_amount", label: "Total Amount", money: true },
    { key: "item_count", label: "Payment Items", num: true },
    { key: "succeeded_items", label: "Succeeded", num: true },
    { key: "failed_items", label: "Failed", num: true },
  ],
  failed: [
    { key: "employee_code", label: "Employee Code" },
    { key: "employee_name", label: "Employee Name" },
    { key: "amount", label: "Amount", money: true },
    { key: "status", label: "Status" },
    { key: "failure_reason", label: "Failure Reason" },
    { key: "attempts", label: "Attempts", num: true },
    { key: "last_attempt_at", label: "Last Attempt" },
  ],
  "bank-wise": [
    { key: "bank_code", label: "Bank Code" },
    { key: "bank_name", label: "Bank Name" },
    { key: "item_count", label: "Payments", num: true },
    { key: "total_amount", label: "Total Amount", money: true },
    { key: "succeeded_items", label: "Succeeded", num: true },
    { key: "failed_items", label: "Failed", num: true },
  ],
  "branch-wise": [
    { key: "bank_name", label: "Bank" },
    { key: "branch_name", label: "Branch" },
    { key: "ifsc_code", label: "IFSC Code" },
    { key: "item_count", label: "Payments", num: true },
    { key: "total_amount", label: "Total Amount", money: true },
  ],
  department: [
    { key: "department_name", label: "Department" },
    { key: "item_count", label: "Payments", num: true },
    { key: "total_amount", label: "Total Amount", money: true },
  ],
};
COLUMNS.pending = COLUMNS.failed;

const formatMoney = (v) =>
  `₹${Number(v ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function formatCell(row, col) {
  const v = row[col.key];
  if (col.money) return formatMoney(v);
  if (v == null || v === "") return "—";
  if (col.key === "last_attempt_at") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  }
  return String(v);
}

/**
 * Consolidated disbursement report explorer. Each report hits a single endpoint.
 *
 * Props: payPeriodId (optional initial pay period; can be changed in the filters).
 */
export default function DisbursementReports({ payPeriodId: initialPeriodId = "" }) {
  const toast = useToast();
  const nav = useReportsNavigation({ payrollReports: PAYROLL_HUB_REPORTS });
  const [theme, setTheme] = useState("light");

  const [tab, setTab] = useState("bank-transfer");
  const [runId, setRunId] = useState("");
  const [payPeriodId, setPayPeriodId] = useState(initialPeriodId || "");
  const [periodLabel, setPeriodLabel] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [generatedAt, setGeneratedAt] = useState(null);
  const [departments, setDepartments] = useState([]);

  const report = REPORTS.find((r) => r.id === tab) || REPORTS[0];
  const columns = useMemo(() => COLUMNS[tab] || [], [tab]);
  const waitingForPeriod = report.needsPeriod && !payPeriodId;

  useEffect(() => {
    API.get("/setup/departments")
      .then((r) => setDepartments(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDepartments([]));
  }, []);

  const deptName = useCallback(
    (id) => {
      if (!id) return "Unassigned";
      const d = departments.find((x) => String(x.id ?? x.department_id) === String(id));
      return d?.name || String(id);
    },
    [departments]
  );

  const load = useCallback(async () => {
    setError("");
    setRows([]);
    if (waitingForPeriod) return;
    setLoading(true);
    try {
      let res;
      switch (tab) {
        case "bank-transfer":
          res = await getBankTransferRegister();
          break;
        case "salary-register":
          res = await getSalaryPaymentRegister(payPeriodId);
          break;
        case "failed":
          res = await getFailedPayments(payPeriodId || undefined);
          break;
        case "pending":
          res = await getPendingPayments(payPeriodId || undefined);
          break;
        case "bank-wise":
          res = await getBankWiseSummary(payPeriodId || undefined);
          break;
        case "branch-wise":
          res = await getBranchWiseSummary(payPeriodId || undefined);
          break;
        case "department":
          res = await getDepartmentWiseSummary(payPeriodId || undefined);
          break;
        default:
          res = { rows: [] };
      }
      setRows(res?.rows || []);
      setGeneratedAt(new Date());
    } catch (e) {
      setError(bankApiError(e) || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [tab, payPeriodId, waitingForPeriod]);

  useEffect(() => {
    load();
  }, [load]);

  // Department summary returns ids; show names.
  const displayRows = useMemo(
    () => (tab === "department" ? rows.map((r) => ({ ...r, department_name: deptName(r.department_id) })) : rows),
    [rows, tab, deptName]
  );

  const totals = useMemo(() => {
    const t = {};
    columns.forEach((c) => {
      if (c.money || c.num) t[c.key] = displayRows.reduce((a, r) => a + Number(r[c.key] ?? 0), 0);
    });
    return t;
  }, [columns, displayRows]);

  const amountKey = columns.find((c) => c.money)?.key;

  const exportRows = () =>
    displayRows.map((r) => Object.fromEntries(columns.map((c) => [c.label, formatCell(r, c)])));

  const exportData = (kind) => {
    if (!displayRows.length) {
      toast.error("No records to export.");
      return;
    }
    const data = exportRows();
    const file = `disbursement_${tab}`;
    try {
      if (kind === "pdf") exportRowsToPdf(data, report.label, `${file}.pdf`);
      else if (kind === "csv") {
        const cols = Object.keys(data[0]);
        const csv = [
          cols.join(","),
          ...data.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")),
        ].join("\n");
        downloadBlob(new Blob(["﻿", csv], { type: "text/csv;charset=utf-8" }), `${file}.csv`);
      } else exportRowsToXlsx(data, report.label.slice(0, 31), `${file}.xlsx`);
      toast.success(`${kind.toUpperCase()} export downloaded.`);
    } catch {
      toast.error("Export failed. Please try again.");
    }
  };

  const printReport = () => {
    if (!displayRows.length) {
      toast.error("No records to print.");
      return;
    }
    printRowsAsTable({
      title: report.label,
      subtitle: [
        payPeriodId && periodLabel ? `Pay Period: ${periodLabel}` : "All Pay Periods",
        `Rows: ${displayRows.length}`,
        amountKey ? `Total: ${formatMoney(totals[amountKey])}` : null,
      ]
        .filter(Boolean)
        .join("  |  "),
      columns: columns.map((c) => c.label),
      rows: exportRows(),
      keyColumns: columns.filter((c) => /employee (code|name)/i.test(c.label)).map((c) => c.label),
    });
  };

  const resetFilters = () => {
    setTab("bank-transfer");
    setRunId("");
    setPayPeriodId("");
    setPeriodLabel("");
  };

  return (
    <div className="prw" data-theme={theme}>
      <ReportsEnterpriseShell
        categoryOrder={nav.categoryOrder}
        reportsByCategory={nav.reportsByCategory}
        selectedKey="bank_disbursement"
        selectedReport={nav.selectedReport}
        reportSearch={nav.reportSearch}
        onReportSearchChange={nav.onReportSearchChange}
        favorites={nav.favorites}
        onToggleFavorite={nav.onToggleFavorite}
        recentReports={nav.recentReports}
        favReports={nav.favReports}
        onSelectReport={nav.onSelectReport}
        onThemeChange={setTheme}
        theme={theme}
        moduleTitle="Banking Reports"
      >
        <div className="prw__body">
          <div className="prw__standard">
            {/* Page header */}
            <div className="prw__page-header">
              <div className="prw__page-header-main">
                <div className="prw__page-icon">
                  <Landmark size={26} />
                </div>
                <div>
                  <h1 className="prw__page-title">Disbursement Reports</h1>
                  <p className="prw__page-desc">
                    Salary bank transfers — registers, failed and pending payments, and bank / branch / department summaries.
                  </p>
                  <div className="prw__page-meta">
                    <span className="prw__meta-chip">
                      <Table2 size={13} /> Report: <b>{report.label}</b>
                    </span>
                    <span className="prw__meta-chip">
                      <CalendarDays size={13} /> Pay Period: <b>{payPeriodId ? periodLabel || "Selected" : "All"}</b>
                    </span>
                    <span className="prw__meta-chip">
                      Rows: <b>{displayRows.length}</b>
                    </span>
                    {amountKey && (
                      <span className="prw__meta-chip">
                        Total: <b>{formatMoney(totals[amountKey])}</b>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Filter card */}
            <div className="prw__filter-card">
              <p className="prw__filter-card-title">
                <Filter size={16} /> Report Filters
              </p>
              <div className="prw__filter-grid">
                <div className="prw__filter">
                  <label htmlFor="dsb-report">
                    <Table2 size={12} /> Report
                  </label>
                  <select id="dsb-report" value={tab} onChange={(e) => setTab(e.target.value)}>
                    {REPORTS.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.label}
                        {r.needsPeriod ? " (needs pay period)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="prw__filter">
                  <PayrollRunSelect
                    label={report.needsPeriod ? "Pay Period (required)" : "Pay Period"}
                    value={runId}
                    onChange={(id, opt) => {
                      setRunId(id);
                      setPayPeriodId(opt?.pay_period_id ? String(opt.pay_period_id) : "");
                      setPeriodLabel(opt?.label || "");
                    }}
                    allowEmpty
                    emptyLabel="All pay periods"
                    hint=""
                  />
                </div>
                <div className="prw__filter-actions">
                  <button
                    type="button"
                    className="prw__btn prw__btn--generate"
                    onClick={load}
                    disabled={loading || waitingForPeriod}
                    aria-busy={loading}
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                    Generate
                  </button>
                  <button type="button" className="prw__btn prw__btn--ghost" onClick={resetFilters}>
                    <RotateCcw size={16} /> Reset
                  </button>
                </div>
              </div>
              <p className="prw__filter-note" style={{ display: "flex", gap: 6, alignItems: "flex-start", margin: "10px 2px 0", fontSize: 12.5, color: "#475569" }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: 2, color: "#2563eb" }} /> {report.info}
              </p>
            </div>

            {/* Action toolbar */}
            <div className="prw__toolbar">
              <button type="button" className="prw__btn prw__btn--refresh prw__btn--sm" onClick={load} disabled={loading || waitingForPeriod}>
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
              <button type="button" className="prw__btn prw__btn--excel prw__btn--sm" onClick={() => exportData("xlsx")}>
                <FileSpreadsheet size={14} /> Excel
              </button>
              <button type="button" className="prw__btn prw__btn--pdf prw__btn--sm" onClick={() => exportData("pdf")}>
                <FileText size={14} /> PDF
              </button>
              <button type="button" className="prw__btn prw__btn--csv prw__btn--sm" onClick={() => exportData("csv")}>
                <Download size={14} /> CSV
              </button>
              <button type="button" className="prw__btn prw__btn--print prw__btn--sm" onClick={printReport}>
                <Printer size={14} /> Print
              </button>
              {generatedAt && (
                <span style={{ marginLeft: "auto", fontSize: 12, color: "#64748b" }}>
                  Last generated: {generatedAt.toLocaleString()}
                </span>
              )}
            </div>

            {error && (
              <div className="prw__error" role="alert">
                <AlertTriangle size={16} /> {error}
              </div>
            )}

            <div className="prw__content">
              <div className="prw__report">
                {waitingForPeriod ? (
                  <div className="prw__empty">
                    <div className="prw__empty-illus"><CalendarDays size={36} /></div>
                    <h3>Select a pay period</h3>
                    <p>{report.label} is generated per pay period. Choose one in the filters above.</p>
                  </div>
                ) : loading ? (
                  <div className="prw__skeleton" aria-busy="true" aria-label="Loading report">
                    <div className="prw__skeleton-row" />
                    <div className="prw__skeleton-row" />
                    <div className="prw__skeleton-row" />
                  </div>
                ) : !displayRows.length ? (
                  <div className="prw__empty">
                    <div className="prw__empty-illus"><Inbox size={36} /></div>
                    <h3>No records found</h3>
                    <p>No disbursement records match this report{payPeriodId ? " and pay period" : ""}. Payments appear here once a salary batch is sent to the bank.</p>
                  </div>
                ) : (
                  <div className="prw__grid-wrap prw__grid-wrap--comfortable">
                    <table className="prw__grid" aria-label={report.label}>
                      <thead>
                        <tr>
                          {columns.map((c) => (
                            <th key={c.key} className={c.money || c.num ? "num" : ""}>{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {displayRows.map((row, i) => (
                          <tr key={i}>
                            {columns.map((c) => (
                              <td key={c.key} className={c.money || c.num ? "num" : ""}>{formatCell(row, c)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          {columns.map((c, idx) => (
                            <td key={c.key} className={c.money || c.num ? "num" : ""}>
                              {idx === 0
                                ? `Totals (${displayRows.length})`
                                : c.money
                                  ? formatMoney(totals[c.key])
                                  : c.num && c.key !== "attempts"
                                    ? totals[c.key]
                                    : ""}
                            </td>
                          ))}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </ReportsEnterpriseShell>
    </div>
  );
}
